import type { FC } from 'react';
import Button from '@components/Button';
import { Show } from '@components/Show';
import { MODAL_Z_INDEX } from '@constants/z-indices';
import { useBlockEdit, useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { useEntityStore } from '@ContractBuilder/store';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { authProvider } from '@helpers/auth';
import { ExclamationIcon, XIcon } from '@heroicons/react/outline';
import { mdiOpenInNew } from '@mdi/js';
import { Icon } from '@mdi/react';
import { isClauseBlock } from '@root/helpers/blocks';
import clsx from 'clsx';

interface BlockUsedInTemplatesModalProps {
  isOpen: boolean;
  onCancel: () => void;
}

export const getTemplatesUrl = () => {
  const href = typeof window === 'undefined' ? '' : window?.location?.href;
  return href.substring(0, href.indexOf('/templates/')) + '/templates';
};

interface BlockUsedWithinProps {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  templateId?: string;
  message: string;
}

export const BlockUsedWithin: FC<BlockUsedWithinProps> = ({ isOpen, onCancel, onSubmit, templateId = '', message }) => {
  'use memo';

  const templatesUrl = getTemplatesUrl();
  const { usedInTemplates } = useBlockEditFormStore(({ formValues }) => ({
    usedInTemplates: formValues?.usedInTemplates ?? [],
  }));
  const { isLoading } = useEntityStore(({ isLoading }) => ({ isLoading }));

  const handleLinkClick = async (templateId: string) => {
    const searchParams = await authProvider.getAuthSearchParams();
    const win = window.open(`${templatesUrl}/${templateId}/contract${searchParams}`);
    return win?.focus();
  };

  return (
    <Show when={isOpen}>
      <Dialog open={isOpen} as="div" className={clsx('fixed inset-0', MODAL_Z_INDEX)} onClose={onCancel}>
        <div
          className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0"
          data-testid="block-used-in-templates-modal"
        >
          <div className="bg-info-500/75 fixed inset-0 transition-opacity" />

          <DialogPanel
            transition
            className="flex h-screen items-center justify-center duration-300 ease-out data-closed:transform-[scale(95%)] data-closed:opacity-0"
          >
            <div className="relative inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle">
              <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                <button
                  type="button"
                  className="text-info-400 hover:text-info-500 rounded-md bg-white focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:outline-hidden"
                  onClick={onCancel}
                >
                  <span className="sr-only">Cancel</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="bg-error-100 mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationIcon className="text-error-600 h-6 w-6" aria-hidden="true" />
                </div>
                <div className="mt-3 flex flex-col gap-4 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <DialogTitle as="h3" className="text-info-900 text-lg leading-6 font-medium">
                    {message}
                  </DialogTitle>
                  <ul role="list" className="flex h-96 flex-col gap-5 overflow-y-auto">
                    {usedInTemplates
                      ?.filter(({ id }) => id !== templateId)
                      ?.map(({ id, name }) => (
                        <li key={id} className="group relative flex justify-between bg-white pr-8 pl-0">
                          <span
                            className="text-primary-500 group-hover:text-primary-600 mr-1 inline-block max-w-[220px] cursor-pointer truncate text-sm leading-[17px] font-medium hover:underline"
                            onClick={() => handleLinkClick(id)}
                          >
                            {name}
                          </span>
                          <span className="relative">
                            <Icon
                              className="text-primary-500 group-hover:text-primary-600 absolute -top-px inline-block"
                              path={mdiOpenInNew}
                              size={0.7}
                            />
                          </span>
                        </li>
                      ))}
                  </ul>
                  <p className="text-info-500 text-sm">Are you sure you want to continue?</p>
                </div>
              </div>
              <div className="mt-5 items-center justify-end gap-x-2.5 sm:mt-4 sm:flex">
                <Button
                  className="h-auto px-4 py-2"
                  onClick={onCancel}
                  kind="secondary"
                  size="sm"
                  isDisabled={isLoading}
                >
                  Cancel
                </Button>
                <Button className="h-auto px-4 py-2" onClick={onSubmit} kind="danger" size="sm" loading={isLoading}>
                  Continue
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </Show>
  );
};

export const BlockUsedInTemplatesModal: FC<BlockUsedInTemplatesModalProps> = ({ isOpen, onCancel }) => {
  const { submissionId } = useEntityStore(({ submission }) => ({ submissionId: submission?.id ?? '' }));
  const { onSubmit } = useBlockEdit();
  const isClause = useBlockEditFormStore(({ formValues }) => Boolean(formValues && isClauseBlock(formValues)));

  return (
    <BlockUsedWithin
      message={`This ${isClause ? 'clause' : 'block'} will also be updated on these templates`}
      isOpen={isOpen}
      onCancel={onCancel}
      onSubmit={onSubmit}
      templateId={submissionId}
    />
  );
};
